import store from '@/store';
import { ticketPriceByCategory } from 'utils/Reserbus';

/**
 * Groups tickets by passenger type and calculates pricing.
 * Iterates through passengers and fragments to find matching tickets,
 * then delegates to ticketPriceByCategory for aggregation.
 *
 * @param {Array<Object>} fragments - Array of fragment objects, each containing a tickets array.
 *   Each ticket should have passengerId and either total or amount property.
 * @param {Array<Object>} passengers - Array of passenger objects with category and id properties.
 * @param {boolean} useTotalPricing - If true, uses ticket.total; if false, uses ticket.amount.
 * @returns {Array<Object>} Result from ticketPriceByCategory with aggregated pricing by category.
 */
function groupByPassengerType(fragments, passengers, useTotalPricing) {
  const preparedTickets = [];

  passengers.forEach(({ category, id }) => {
    fragments.forEach(({ tickets }) => {
      const passengerTicket = tickets.find(({ passengerId }) => passengerId === id);

      if (useTotalPricing) {
        // New behavior: use total with fallback to amount
        const total = passengerTicket ? passengerTicket.total ?? passengerTicket.amount ?? 0 : 0;
        preparedTickets.push({ category, total });
      } else {
        // Original behavior: use amount
        const amount = passengerTicket ? passengerTicket.amount : 0;
        preparedTickets.push({ category, amount });
      }
    });
  });

  return ticketPriceByCategory(preparedTickets, fragments.length, useTotalPricing);
}

/**
 * Groups tickets by bus category and handles infant passengers.
 * Copies all tickets from fragments and adds zero-price entries for infants
 * (since infants don't generate tickets for buses), then delegates to ticketPriceByCategory.
 *
 * @param {Array<Object>} fragments - Array of fragment objects, each containing a tickets array.
 * @param {Array<Object>} passengers - Array of passenger objects with category property.
 * @param {boolean} useTotalPricing - If true, infant tickets use total:0; if false, use amount:0.
 * @returns {Array<Object>} Result from ticketPriceByCategory with aggregated pricing by category.
 */
function groupByBusTicketCategory(fragments, passengers, useTotalPricing) {
  const ticketsCopy = [];

  fragments.forEach((fragment) => {
    ticketsCopy.push(...fragment.tickets.map((ticket) => ({ ...ticket })));
    // infants don't generate tickets for buses, so the price is 0
    passengers.forEach(({ category }) => {
      if (category === 'infant') {
        const infantTicket = useTotalPricing ? { category, total: 0 } : { category, amount: 0 };
        ticketsCopy.push(infantTicket);
      }
    });
  });

  return ticketPriceByCategory(ticketsCopy, fragments.length, useTotalPricing);
}

/**
 * Prepares ticket pricing data based on transport type and feature flags.
 * Returns general pricing if fragments have no tickets or displayPricingBeforeCheckout is true.
 * Otherwise, delegates to groupByBusTicketCategory for bus transport or groupByPassengerType for others.
 *
 * @param {string} transportType - The type of transport (e.g., 'bus', 'flight').
 * @param {Array<Object>} fragments - Array of fragment objects containing tickets.
 * @param {Array<Object>} passengers - Array of passenger objects.
 * @param {number} passengerCount - Total number of passengers.
 * @param {number} amount - Total amount for general pricing fallback.
 * @param {boolean} displayPricingBeforeCheckout - If true, returns general pricing.
 * @returns {Array<Object>} Array of pricing objects with category, count, and total.
 */
function prepareTicketsPricing(
  transportType,
  fragments,
  passengers,
  passengerCount,
  amount,
  displayPricingBeforeCheckout,
) {
  const { features } = store.getState().whitelabelConfig;
  const useTotalPricing = features.USE_TOTAL_FOR_TICKET_PRICING ?? false;

  if (fragments.some(({ tickets }) => tickets.length < 1) || displayPricingBeforeCheckout) {
    return [
      {
        category: 'general',
        count: passengerCount,
        total: amount,
      },
    ];
  }

  if (transportType === 'bus') {
    return groupByBusTicketCategory(fragments, passengers, useTotalPricing);
  }

  return groupByPassengerType(fragments, passengers, useTotalPricing);
}

export default prepareTicketsPricing;
